import { Component, ElementRef, HostBinding, input, Input, OnInit, output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Icons } from '../ce-icon/ICONLIB';
import { CeIconComponent } from '../ce-icon/ce-icon.component';

export type CePillState = 'default' | 'error' | 'success' | 'warning' | 'info' | 'highlight' | 'emphasis';
const PILL_STYLES = <const>['primary', 'secondary', 'tertiary', 'code'];
export type CePillStyle = (typeof PILL_STYLES)[number];

@Component({
    selector: `ce-pill, ce-pill-xs, ce-pill-sm, ce-pill-md, ce-pill-lg,
    ce-pill-primary, ce-pill-secondary, ce-pill-tertiary, ce-pill-code`,
    imports: [CommonModule, CeIconComponent],
    templateUrl: './ce-pill.component.html',
    styleUrl: './ce-pill.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class CePillComponent implements OnInit {
    /**
     * The 'state' input property allows you to set the state of the pill component.
     * It accepts one of the following values: 'default', 'error', 'success', 'warning', 'info'.
     * Depending on the state, the pill component will have different styles.
     * By default, the state is set to 'default'.
     */
    @Input() state: CePillState = 'default';
    @Input() icon?: keyof Icons;
    /**
     * Optional suffix text displayed after the main pill content, such as "+4".
     */
    suffix = input<string | undefined>();

    /**
     * When true, displays a remove button (×) that emits the 'remove' event when clicked.
     */
    removable = input<boolean>();

    /**
     * Emits when the remove button is clicked. Only applicable when 'removable' is true.
     */
    remove = output<MouseEvent>();
    
    // Define additional pill sizes, style
    additionalPillSizes: string[] = ['ce-pill-xs', 'ce-pill-sm', 'ce-pill-md', 'ce-pill-lg'];
    additionalPillStyles: string[] = ['ce-pill-primary', 'ce-pill-secondary', 'ce-pill-tertiary', 'ce-pill-code'];
    selectedPillSize = 'ce-pill-sm';
    selectedPillStyle = 'ce-pill-primary';

    @HostBinding('class') get className(): string {
        const size = this.selectedPillSize.split('-').pop();
        const style = this.selectedPillStyle.split('-').pop();
        return `CePill CePill--${size} CePill--${style} CePill--${this.state}`;
    }

    constructor(private elem: ElementRef) {}

    ngOnInit() {
        this.#setPillAttributes();
    }

    onRemove(event: MouseEvent): void {
        this.remove.emit(event);
    }

    #setPillAttributes() {
        const pillElement = this.elem.nativeElement as HTMLElement;

        for (const pillSize of this.additionalPillSizes) {
            if (pillElement.hasAttribute(pillSize)) {
                this.selectedPillSize = pillSize;
                break;
            }
        }

        for (const pillStyle of this.additionalPillStyles) {
            if (pillElement.hasAttribute(pillStyle)) {
                this.selectedPillStyle = pillStyle;
                break;
            }
        }
    }
}
